import Scrollbar from "@@/components/Scrollbar";
import Box from "@@/elements/Box";
import Typhography from "@@/elements/Typography";
import { useFocusReturn } from "@@/hooks/use-focus-trap";
import useOutsideClick from "@@/hooks/use-outside-click";
import cn from "classnames";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { Item, SelectorVariant } from "../types";
import styles from "./styles.module.scss";

type Props = {
  items: Item[];
  open: boolean;
  variant: SelectorVariant;
  close: () => void;
  id: string;
  addTracking?: boolean;
  triggerRef: RefObject<HTMLButtonElement>;
  listId: string;
};

const LanguageSelectorDropdown = ({
  items, open, variant, close, id, addTracking, triggerRef, listId,
} : Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement>(null);

  const classNames = cn(styles.wrapper_inner, {
    [styles.wrapper_inner__header]: variant === "header",
    [styles.wrapper_inner__footer]: variant === "footer",
    [styles.wrapper_inner__open]: open,
  });

  useOutsideClick(ref, (e: MouseEvent) => {
    const button = document.getElementById(id);
    if (button?.contains(e.target as Node)) return;
    close();
  });

  useFocusReturn({
    containerRef: ref,
    triggerRef,
    isActive: open,
    onEscape: close,
  });

  useEffect(() => {
    if (!open || !listRef.current) return;
    const firstLink = listRef.current.querySelector<HTMLElement>("a[href]");
    if (firstLink) {
      setTimeout(() => firstLink.focus(), 0);
    }
  }, [open]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();

    const list = listRef.current;
    if (!list) return;

    const links = Array.from(list.querySelectorAll<HTMLElement>("a[href]"));
    if (links.length === 0) return;

    const currentIndex = links.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;

    if (event.key === "ArrowDown") {
      nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
    }

    links[nextIndex].focus();
  }, []);

  return (
    <div
      className={styles.wrapper}
      ref={ref}
      inert={!open ? "" : undefined}
    >
      <Box className={classNames}>
        <Scrollbar className={styles.scrollarea}>
          <Box
            flexDirection="column"
            className={styles.list}
            component="ul"
            ref={listRef}
            id={listId}
            aria-label={items?.find((i) => i.active)?.link.text}
            onKeyDown={handleKeyDown}
          >
            {items.map(({ active, link }, index) => {
              if (active) {
                return (
                  <li
                    key={index}
                    aria-current="page">
                    <Typhography
                      variant="toolbarMenu"
                      color="ComplementaryCharcoalTint04">
                      {link.text}
                    </Typhography>
                  </li>
                );
              }
              return (
                <li key={index}>
                  <a
                    href={link.href}
                    target={link.target}
                    title={link.title}
                    data-menu-type={addTracking ? "menu utilities" : undefined}
                    data-menu-level={addTracking ? "2" : undefined}
                    data-menu-text={addTracking ? link.text : undefined}
                    data-menu-path={addTracking ? link.href : undefined}>
                    <Typhography
                      variant="toolbarMenu"
                      color="PrimaryGrey"
                      asElement="span">
                      {link.text}
                    </Typhography>
                  </a>
                </li>
              );
            })}
          </Box>
        </Scrollbar>
      </Box>
    </div>
  );
};

export default LanguageSelectorDropdown;
